/**
 * WHICH MEETING INTELLIGENCE EVENTS ARE ABOUT ONE MEETING — a pure rule, kept
 * apart from the subscription wrapper (meetingIntelligenceRealtime.ts) so a
 * test that stubs the socket still runs the real rule.
 */
import type { MeetingIntelEvent } from "realtime";

/**
 * Does a Meeting Intelligence event change what a page about ONE meeting
 * shows? The one answer Meeting Detail's tabs share (the meeting record and
 * its review), so they cannot disagree about which events move them. PURE.
 *
 * Besides the analysis landing (`meeting-processed` from HQ's own pass, then
 * `meeting-enriched` once that pass has written its analyses; `meeting-analyzed`
 * from a producer's stored push) and its items being distributed
 * (`meeting-distributed`: the assignments now exist) and its mapping to a
 * client (`meeting-mapped`, which names it), the page changes when a
 * COLLEAGUE acts on its items (#3820): a close or reopen (the item row, with
 * its `meetingId`), a bulk close of this meeting's checklist (an envelope
 * naming the meeting), a stale sweep (an envelope across meetings, so any
 * sweep that closed something), and an accept / reject / complete or module
 * move of an item's assignment (only the `assignmentId` is published, matched
 * against `assignmentIds`, the page's items' assignments as strings).
 */
export function isAboutMeeting(
  event: MeetingIntelEvent,
  meetingId: number,
  assignmentIds: ReadonlySet<string>
): boolean {
  const payload = event.payload;
  const isThisMeeting = (value: unknown) =>
    value !== undefined && value !== null && Number(value) === meetingId;
  switch (event.kind) {
    case "meeting-processed":
    case "meeting-analyzed":
      return isThisMeeting(payload.meetingId ?? payload.id);
    case "meeting-enriched":
    case "meeting-distributed":
    case "meeting-mapped": // mapped to a client (2026-11-02): the page names its client
      return isThisMeeting(payload.meetingId);
    case "action-item-changed":
      return isThisMeeting(payload.meetingId);
    case "action-items-closed":
      if (payload.meetingId !== undefined && payload.meetingId !== null) {
        return isThisMeeting(payload.meetingId);
      }
      return Number(payload.closed) > 0;
    case "assignment-accepted":
    case "assignment-rejected":
    case "assignment-completed":
    case "assignment-module-changed": {
      const assignmentId = payload.assignmentId;
      if (assignmentId === undefined || assignmentId === null) return false;
      return assignmentIds.has(String(assignmentId));
    }
    default:
      return false;
  }
}
